import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

interface Override {
  values: string[];
  contextName: string;
}

interface Constraint {
  values: string[];
  inverted: boolean;
  operator: string;
  contextName: string;
  caseInsensitive: boolean;
}

export interface Variant {
  name: string;
  weight: number;
  overrides: Override[];
  payload: {
    type: string;
    value: string;
  };
  stickiness: string;
  weightType: string;
}

export interface Strategy {
  name: string;
  constraints: Constraint[];
  variants: Variant[];
  parameters: {
    groupId: string;
    rollout: string;
    stickiness: string;
  };
  sortOrder: number;
  id: string;
  title: string;
  disabled: boolean;
}

interface Environment {
  name: string;
  lastSeenAt: string;
  enabled: boolean;
  type: string;
  sortOrder: number;
  strategies: Strategy[];
}

export interface UnleashFeature {
  name: string;
  impressionData: boolean;
  description: string | null;
  lastSeenAt: string | null;
  createdAt: string;
  variants: Variant[];
  environments: Environment[];
}

interface CreateFeatureArgs {
  name: string;
  type?: string;
  description?: string;
  impressionData?: boolean;
}

interface UpdateFeatureArgs {
  name: string;
  description?: string;
  type?: string;
  stale?: boolean;
  archived?: boolean;
  impressionData?: boolean;
}

interface UpdateFeatureVariantsArgs {
  name: string;
  variants: {
    name: string;
    weight: number;
    weightType: "variable";
    stickiness?: string;
    payload?: {
      type: string;
      value: string;
    };
    overrides?: Override[];
  }[];
  environment?: string;
}

interface UpdateStrategyVariantsArgs {
  featureName: string;
  environment: string;
  strategyId: string;
  strategy: Strategy
}

interface GetStrategiesArgs {
  featureName: string;
  environment: string;
}

interface CreateStrategyArgs {
  featureName: string;
  environment: string;
  strategy: {
    name: string;
    parameters?: Record<string, string>;
    variants?: {
      name: string;
      weight: number;
      weightType: "variable";
      stickiness?: string;
      payload?: {
        type: string;
        value: string;
      };
      overrides?: Override[];
    }[];
  };
}


export const unleashApi = createApi({
  reducerPath: "unleash",
  tagTypes: ["features", "strategy"],
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/unleash",
  }),
  endpoints: (builder) => ({
    getFeatures: builder.query<UnleashFeature[], void>({
      providesTags: ["features"],
      query: () => ({
        url: `/features`,
      }),
    }),
    getFeature: builder.query<UnleashFeature, string>({
      providesTags: ["features"],
      query: (featureName) => ({
        url: `/features/${featureName}`,
      }),
    }),
    getStrategies: builder.query<Strategy[], GetStrategiesArgs>({
      providesTags: ["strategy"],
      query: ({ featureName, environment }) => ({
        url: `/features/${featureName}/environments/${environment}/strategies`,
      }),
    }),
    getStrategy: builder.query<Strategy, { featureName: string; environment: string; strategyId: string }>({
      providesTags: ["strategy"],
      query: ({ featureName, environment, strategyId }) => ({
        url: `/features/${featureName}/environments/${environment}/strategies/${strategyId}`,
      }),
    }),
    createStrategy: builder.mutation<Strategy, CreateStrategyArgs>({
      invalidatesTags: ["strategy"],
      query: ({ featureName, environment, strategy }) => ({
        url: `/features/${featureName}/environments/${environment}/strategies`,
        method: "POST",
        body: strategy,
      }),
    }),

    createFeature: builder.mutation<UnleashFeature, CreateFeatureArgs>({
      query: (data) => ({
        url: `/features`,
        method: "POST",
        body: data,
      }),
    }),
    updateFeature: builder.mutation<UnleashFeature, UpdateFeatureArgs>({
      query: ({ name, ...data }) => ({
        url: `/features/${name}`,
        method: "PUT",
        body: data,
      }),
    }),
    updateFeatureVariants: builder.mutation<
      UnleashFeature,
      UpdateFeatureVariantsArgs
    >({
      query: ({ name, variants }) => ({
        url: `/features/${name}/variants`,
        method: "PUT",
        body: variants,
      }),
    }),
    updateFeatureEnvironmentVariants: builder.mutation<
      UnleashFeature,
      UpdateFeatureVariantsArgs
    >({
      query: ({ name, variants, environment }) => ({
        url: `/features/${name}/environments/${environment}/variants`,
        method: "PUT",
        body: variants,
      }),
    }),
    updateStrategyVariants: builder.mutation<
      UnleashFeature,
      UpdateStrategyVariantsArgs
    >({
      query: ({ featureName, environment, strategyId, strategy }) => ({
        url: `/features/${featureName}/environments/${environment}/strategies/${strategyId}`,
        method: "PUT",
        body: strategy,
      }),
    }),
    enableFeature: builder.mutation<
      void,
      { featureName: string; environment: string }
    >({
      query: ({ featureName, environment }) => ({
        url: `/features/${featureName}/environments/${environment}/on`,
        method: "POST",
      }),
    }),
    disableFeature: builder.mutation<
      void,
      { featureName: string; environment: string }
    >({
      query: ({ featureName, environment }) => ({
        url: `/features/${featureName}/environments/${environment}/off`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetFeaturesQuery,
  useGetFeatureQuery,
  useGetStrategiesQuery,
  useCreateStrategyMutation,
  useCreateFeatureMutation,
  useUpdateFeatureMutation,
  useUpdateFeatureVariantsMutation,
  useUpdateFeatureEnvironmentVariantsMutation,
  useUpdateStrategyVariantsMutation,
  useEnableFeatureMutation,
  useDisableFeatureMutation,
} = unleashApi;
